// 指標の値 → 色。地図の円・一覧のバー・詳細タイル・凡例のグラデーションが同じ関数を通るので、
// 「この色は何を意味するのか」の答えが 1 箇所にしか無い。DOM に依存しない純粋関数。
import type { Muni } from "../../schema/muni";
import { METRIC_GET, type MetricKey, type ScaleKind } from "./metrics";

const SCALES_RISK = ["#14958a", "#8cc26b", "#f2c14e", "#ec7a3a", "#b91c1c"];

export const SCALES: Record<ScaleKind, string[]> = {
  risk: SCALES_RISK,
  thermal: ["#2b5fd9", "#7fb3ff", "#efe9dc", "#f29a4b", "#c0392b"],
  // 「高いほど望ましい」指標は risk と同じ色相を逆向きに使う。緑 = 望ましい端、という
  // 読み方をスケール間で揃えるため、新しい色は増やさず並びだけを反転させる。
  amenity: [...SCALES_RISK].reverse(),
};

/** 値が欠測（null）であることを、スケール上のどの色とも混同しない中立色で表す。 */
export const NULL_COLOR = "#a3a9b3";

/**
 * SSR の詳細タイルは全件データを持たないため分位点を計算できない。
 * 実データの分布（p90: 洪水 0.55 / 津波 0.20 / 土砂 0.12 / 地震 0.70）から、
 * 上位帯が飽和しきらない固定 domain を決め打ちする。
 */
export const DETAIL_TILE_DOMAINS: Record<"flood" | "tsunami" | "sediment" | "quake", [number, number]> = {
  flood: [0, 0.5],
  tsunami: [0, 0.5],
  sediment: [0, 0.5],
  quake: [0, 0.8],
};

/**
 * 詳細の月別気温グラフが使う固定 domain。市区町村ごとに自動スケールすると
 * 「那覇の 1 月」と「旭川の 1 月」が同じ色になってしまうため、全国共通の温度帯で塗る。
 */
export const CLIMATE_TEMP_DOMAIN: [number, number] = [-5, 30];

const DOMAIN_LOW_PERCENTILE = 0.02;
const DOMAIN_HIGH_PERCENTILE = 0.98;

/** 昇順ソート済み配列の線形補間による分位点。 */
function quantile(sorted: number[], p: number): number {
  const pos = (sorted.length - 1) * p;
  const lower = Math.floor(pos);
  const upper = Math.min(sorted.length - 1, lower + 1);
  const a = sorted[lower] ?? 0;
  const b = sorted[upper] ?? a;
  return a + (b - a) * (pos - lower);
}

/**
 * 色分けの下端・上端。地価のように最大 2.3M の外れ値があると min〜max では
 * ほぼ全件が同じ色に潰れるため、p2〜p98 に切り詰める。
 */
export function metricDomain(munis: Muni[], metric: MetricKey): [number, number] {
  const get = METRIC_GET[metric];
  const values = munis
    .map((m) => get(m))
    .filter((v): v is number => v !== null)
    .sort((a, b) => a - b);
  if (values.length === 0) return [0, 1];
  const lo = quantile(values, DOMAIN_LOW_PERCENTILE);
  const hi = quantile(values, DOMAIN_HIGH_PERCENTILE);
  return lo === hi ? [lo, lo + 1] : [lo, hi];
}

function hexToRgb(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function toHex(channel: number): string {
  return Math.round(channel).toString(16).padStart(2, "0");
}

export function colorFor(value: number | null, domain: [number, number], scale: ScaleKind): string {
  if (value === null) return NULL_COLOR;
  const stops = SCALES[scale];
  const [lo, hi] = domain;
  const t = hi > lo ? Math.min(1, Math.max(0, (value - lo) / (hi - lo))) : 0;

  const pos = t * (stops.length - 1);
  const i = Math.min(stops.length - 2, Math.floor(pos));
  const from = stops[i];
  const to = stops[i + 1];
  if (from === undefined || to === undefined) return NULL_COLOR;
  const frac = pos - i;
  if (frac === 0) return from;
  if (frac === 1) return to;

  const a = hexToRgb(from);
  const b = hexToRgb(to);
  return `#${toHex(a[0] + (b[0] - a[0]) * frac)}${toHex(a[1] + (b[1] - a[1]) * frac)}${toHex(a[2] + (b[2] - a[2]) * frac)}`;
}

export function gradientCss(scale: ScaleKind): string {
  const stops = SCALES[scale];
  const parts = stops.map((c, i) => `${c} ${((i / (stops.length - 1)) * 100).toFixed(0)}%`);
  return `linear-gradient(90deg, ${parts.join(", ")})`;
}
